import React from 'react';
import cls from 'classnames';

import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { Formik, FormikErrors, FormikHelpers } from 'formik';

import { TranslationContext, ConfigContext } from '@wix/form-fields';

import { useFileUploader } from 'common/hooks';

import { getDateTimeFieldErrors } from 'wui/DateTimeField';
import { isFile } from 'wui/ImageViewer';
import { TPAComponentProps } from 'wui/types';

import { IEventForm } from '../EventForm/types';

import classes from './EventPage.scss';

export interface IEventFormProps
  extends React.ComponentProps<typeof Formik<IEventForm>>,
    TPAComponentProps {
  onBusyStateChange?(busy: boolean): void;
  children: React.ReactNode;
  className?: string;
}

export function EventPage(props: IEventFormProps) {
  const { onBusyStateChange, onSubmit, children, className, ...rest } = props;

  const uploader = useFileUploader();

  const { t, i18n } = useTranslation();
  const { isMobile } = useEnvironment();

  return (
    <TranslationContext.Provider
      value={{
        i18n,
        t,
      }}
    >
      <ConfigContext.Provider
        value={{
          locale: i18n.language,
          regionalFormat: i18n.language,
          isMobile,
        }}
      >
        <Formik validate={handleValidate} onSubmit={handleSubmit} {...rest}>
          {(formikProps) => (
            <form
              data-hook={rest['data-hook']}
              noValidate
              onReset={formikProps.handleReset}
              onSubmit={formikProps.handleSubmit}
              className={cls(classes.root, { [classes.mobile]: isMobile })}
            >
              {children}
            </form>
          )}
        </Formik>
      </ConfigContext.Provider>
    </TranslationContext.Provider>
  );

  async function handleSubmit(
    values: IEventForm,
    helpers: FormikHelpers<IEventForm>,
  ) {
    if (!props.onSubmit) {
      return;
    }

    if (!isFile(values.cover)) {
      props.onSubmit(values, helpers);
      return;
    }

    try {
      onBusyStateChange?.(true);
      const {
        width,
        height,
        file_name: mediaId,
      } = await uploader.upload(values.cover);

      props.onSubmit(
        {
          ...values,
          cover: {
            image: {
              mediaId,
              width,
              height,
            },
          },
        },
        helpers,
      );
    } catch {
      props.onSubmit(values, helpers);
    } finally {
      onBusyStateChange?.(false);
    }
  }

  function handleValidate(values: IEventForm) {
    const errors: FormikErrors<IEventForm> = {};

    if (!values.name?.trim()) {
      errors.name = t('groups-web.event-form.fields.name.error.empty');
    }

    if (!values.location?.trim()) {
      errors.location = t('groups-web.event-form.fields.location.error.empty');
    }

    if (getDateTimeFieldErrors(values.startDate)) {
      errors.startDate = t(
        'groups-web.event-form.fields.start-date.error.empty',
      );
    }

    if (getDateTimeFieldErrors(values.endDate)) {
      errors.endDate = t('groups-web.event-form.fields.end-date.error.empty');
    }

    const diff =
      new Date(values.endDate).getTime() - new Date(values.startDate).getTime();
    if (!diff || diff < 0) {
      errors.duration = t(
        'groups-web.event-form.fields.duration.error.minValue',
      );
    }

    return errors;
  }
}

EventPage.displayName = 'EventPage';
